
import { useState, useEffect } from 'react';
import { db } from '../firebase/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

export interface FirestoreValueState<T> {
  value: T;
  isHydrated: boolean;
  hasRemoteValue: boolean;
  error: Error | null;
}

/**
 * A read-only hook to listen for changes to a specific field within the app's 
 * main data document ('app-data/main'), accessible to all users.
 * 
 * @param key The key of the field to listen to within the main data document.
 * @param defaultValue The default value to use if the document or key doesn't exist.
 * @returns The current value of the field.
 */
export const useFirestore = <T>(key: string, defaultValue: T): T => {
  return useFirestoreState(key, defaultValue).value;
};

export const useFirestoreState = <T>(key: string, defaultValue: T): FirestoreValueState<T> => {
  const [value, setValue] = useState<T>(defaultValue);
  const [isHydrated, setIsHydrated] = useState(false);
  const [hasRemoteValue, setHasRemoteValue] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const mainDataDocRef = doc(db, 'app-data', 'main');
    
    // Listen directly to the public data document, regardless of auth state.
    const unsubscribeSnapshot = onSnapshot(mainDataDocRef, (docSnapshot) => {
      setError(null);
      setIsHydrated(true);

      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const remoteValue = data[key];
        setHasRemoteValue(remoteValue !== undefined);
        setValue(remoteValue !== undefined ? remoteValue : defaultValue);
      } else {
        // If the main document doesn't exist, use the default value.
        setHasRemoteValue(false);
        setValue(defaultValue);
      }
    }, (error) => {
        console.error(`Error listening to ${key} in app-data:`, error);
        setError(error);
        setIsHydrated(true);
        setHasRemoteValue(false);
        setValue(defaultValue);
    });

    // Cleanup the listener when the component unmounts.
    return () => unsubscribeSnapshot();
  }, [defaultValue, key]); // Re-run only if key changes

  return {
    value,
    isHydrated,
    hasRemoteValue,
    error,
  };
};

export default useFirestore;
